import { ArrowRight } from "lucide-react";
import { Photo } from "./Photo";

const FloatingCard = ({ eyebrow, title, text, className = "" }) => (
  <div className={`absolute z-20 rounded-[12px] border border-line/60 bg-paper/95 p-5 shadow-[0_16px_42px_rgba(20,28,24,0.12)] backdrop-blur-sm sm:p-6 ${className}`}>
    <p className="text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-moss">{eyebrow}</p>
    <h3 className="mt-2 font-display text-[1.55rem] leading-none text-ink sm:text-[1.7rem]">{title}</h3>
    <p className="mt-3 max-w-[15rem] text-sm leading-relaxed text-ink2">{text}</p>
  </div>
);

const SmallProject = ({ name, alt, eyebrow, title, text, position, testId, className = "", cardSide = "left" }) => (
  <div className={`reveal relative pb-16 ${className}`}>
    <figure className="relative h-[250px] overflow-hidden rounded-[14px] bg-sand sm:h-[315px] lg:h-[300px]" data-testid={testId}>
      <Photo name={name} alt={alt} sizes="(min-width: 1024px) 25vw, 100vw" position={position} />
    </figure>
    <div
      className={`absolute bottom-0 z-20 w-[88%] rounded-[12px] border border-line/60 bg-paper/95 p-5 shadow-[0_14px_34px_rgba(20,28,24,0.10)] backdrop-blur-sm ${
        cardSide === "right" ? "right-[-10px]" : "left-[-10px]"
      }`}
    >
      <p className="text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-moss">{eyebrow}</p>
      <div className="mt-2 flex items-end justify-between gap-4">
        <div>
          <h3 className="font-display text-[1.65rem] leading-none text-ink">{title}</h3>
          <p className="mt-2 max-w-[220px] text-sm leading-relaxed text-ink2">{text}</p>
        </div>
      </div>
    </div>
  </div>
);

export const Projects = () => (
  <section id="projekt" aria-labelledby="projekt-title" className="overflow-hidden bg-[#f7f4ee] py-16 md:py-20 lg:py-24" data-testid="projekt-section">
    <div className="mx-auto max-w-[1560px] px-5 sm:px-8 lg:px-12">
      <div className="relative">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-start">
          <div className="reveal relative z-30 lg:col-span-3 lg:pt-6">
            <p className="eyebrow">Projekt</p>
            <h2 id="projekt-title" className="mt-4 max-w-[330px] font-display text-[2.35rem] leading-[0.98] tracking-tight text-ink sm:text-[2.75rem] lg:text-[3rem]">
              Riktiga projekt.<br />Riktiga resultat.
            </h2>
            <p className="mt-4 max-w-[335px] text-[0.97rem] leading-[1.55] text-ink2">
              Här är ett urval av våra senaste arbeten – från renoveringar och tillbyggnader till friggebodar och uterum.
            </p>
            <a href="#kontakt" className="btn-primary relative z-30 mt-6">
              Beskriv ditt projekt <ArrowRight size={17} strokeWidth={1.7} className="arrow" aria-hidden="true" />
            </a>
          </div>

          <div className="reveal relative lg:col-span-9">
            <figure className="relative h-[390px] overflow-hidden rounded-[16px] bg-sand sm:h-[470px] lg:h-[500px]">
              <Photo
                name="projekt-fasad"
                alt="Renoverat hus med ljus träpanel, röda knutar och mörkt tak"
                sizes="(min-width: 1024px) 75vw, 100vw"
                position="52% 43%"
                testId="projekt-image-0"
              />
            </figure>
            <FloatingCard
              eyebrow="Ombyggnad"
              title="Villa i Avesta"
              text="Ny planlösning, uppdaterad fasad och fler sociala ytor."
              className="right-4 top-16 w-[225px] sm:right-5 sm:top-20 sm:w-[270px] lg:-right-3 lg:w-[285px]"
            />
          </div>
        </div>

        <div className="relative z-10 mt-10 grid gap-6 lg:-mt-6 lg:grid-cols-12 lg:items-start lg:gap-7">
          <div className="reveal relative lg:col-span-6">
            <figure className="relative h-[420px] overflow-hidden rounded-[16px] bg-sand sm:h-[490px] lg:h-[475px]">
              <Photo
                name="projekt-trappa"
                alt="Altan med bred trätrappa"
                sizes="(min-width: 1024px) 50vw, 100vw"
                position="49% 52%"
                testId="projekt-image-1"
              />
            </figure>
            <FloatingCard
              eyebrow="Tillbyggnad"
              title="Altan med tak"
              text="Större yta för umgänge, skyddad från väder och vind."
              className="bottom-5 left-5 w-[280px] sm:bottom-7 sm:left-7 sm:w-[320px]"
            />
          </div>

          <div className="lg:col-span-3 lg:pt-[72px] lg:-ml-4">
            <SmallProject
              name="projekt-tillbyggnad"
              alt="Friggebod i trä"
              eyebrow="Friggebod"
              title="Friggebod"
              text="Praktisk extra yta med samma stil som huset."
              position="43% 50%"
              testId="projekt-image-2"
              cardSide="left"
            />
          </div>

          <div className="lg:col-span-3 lg:pt-[30px] lg:-ml-10">
            <SmallProject
              name="hero-altan"
              alt="Altan intill huset"
              eyebrow="Uterum"
              title="Uterum"
              text="Förlänger säsongen och skapar en ljus och härlig plats."
              position="58% 72%"
              testId="projekt-image-3"
              cardSide="right"
              className="lg:z-20"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);